// app/javascript/quotes/components/App.jsx
import React, { useState, useEffect, useMemo } from 'react'
import {
  Switch,
  Route,
  Redirect,
} from 'react-router-dom'
import { useLocation } from 'react-router-dom'

const queryString = require('query-string');

import NetworkLayer from './NetworkLayer'
import LoginDisplay from './LoginDisplay'
import SignUpDisplay from './SignUpDisplay'
import PasswordResetContainer from './PasswordResetContainer'
import SuccessDisplay from './SuccessDisplay'
import AcceptInviteDisplay from './AcceptInviteDisplay'
import ForgotDisplay from './ForgotDisplay'
import Intercom from 'react-intercom';
import InitializeAmplitude from './initialize_amplitude';
import PQL from 'pql/PQL';
import SubscriptionDisplay from './SubscriptionDisplay'
import { useIntercomData } from '../../mappers/IntercomMapper';
import GoogleAnalytics from './GoogleAnalytics'
import AIChatDrawer from './AIChat/AIChatDrawer';
import { useAIChat } from './contexts/AIChatContext';
import { Box } from '@mui/material';
import QualCodeActions from 'actions/QualCodeActions';
import useSentryUser from 'hooks/useSentryUser';

const AppBody = ({ isLoggedIn }) => {
  const location = useLocation();
  const [promo, setPromo] = useState(null);
  const intercomData = useIntercomData();
  const { isOpen: isChatOpen } = useAIChat();

  // Extract promo from URL
  const promoFromUrl = useMemo(() => {
    const parsed = queryString.parse(location.search);
    return parsed.promo || null;
  }, [location.search]);

  // Update promo state when URL changes
  useEffect(() => {
    if (promoFromUrl) {
      setPromo(promoFromUrl);
    }
  }, [promoFromUrl]);

  // Email confirmation redirects to /sign_in?email_confirmed=true, but the user
  // may still have stale auth tokens, making isLoggedIn true. In that case the
  // logged-in routes render and /sign_in is never reached. Sign out and reload
  // so LoginDisplay can show the success message.
  const isRedirectingRef = React.useRef(false);
  const hasConfirmParam = new URLSearchParams(location.search).get('email_confirmed') === 'true';

  useEffect(() => {
    if (hasConfirmParam && isLoggedIn) {
      isRedirectingRef.current = true;
      QualCodeActions.signOut();
      window.location.href = location.pathname + location.search;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tag Sentry events with the current user's numeric id (no email/PII).
  useSentryUser(intercomData?.user_id);

  if (isRedirectingRef.current) return null;
  if (hasConfirmParam && isLoggedIn) return null;

  if (!isLoggedIn) {
    return (
      <div>
        <GoogleAnalytics />
        <InitializeAmplitude apiKey={AMPLITUDE_ID}/>
        <Intercom appID={INTERCOM_ID} custom_launcher_selector='#delve_help_link'/>
        <Switch>
          <Route
            path='/reset'
            component={PasswordResetContainer}
          />
          <Route
            path='/forgot'
            component={ForgotDisplay}
          />
          <Route
            path='/sign_up'
            component={SignUpDisplay}
          />
          <Route
            path='/sign_in'
            component={LoginDisplay}
          />
          <Route
            path='/invite'
            component={AcceptInviteDisplay}
          />
          {/* Default go to sign in page */}
          <Route render={() => <Redirect to="/sign_in" />} />
        </Switch>
      </div>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <GoogleAnalytics userID={intercomData?.user_id}/>
      <InitializeAmplitude apiKey={AMPLITUDE_ID} userId={intercomData?.user_id}/>
      <Intercom appID={INTERCOM_ID} {...intercomData} custom_launcher_selector='#delve_help_link' hide_default_launcher={isChatOpen}/>
      
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: isChatOpen 
              ? theme.transitions.easing.easeOut 
              : theme.transitions.easing.sharp,
            duration: isChatOpen 
              ? theme.transitions.duration.enteringScreen 
              : theme.transitions.duration.leavingScreen,
          }),
          width: isChatOpen ? 'calc(100vw - 400px)' : '100vw',
          overflow: 'hidden', // Prevent content from flowing under drawer during transition
        }}
      >
        <Switch>
          <Route
            path='/sign_up'
            render={() => {
              // Preserve promo parameter when redirecting to intro_survey
              const redirectPath = promoFromUrl 
                ? `intro_survey?promo=${promoFromUrl}`
                : "intro_survey";
              return <Redirect to={redirectPath}/>;
            }}
          />
          <Route
            path='/reset'
            component={PasswordResetContainer}
          />
          <Route
            path='/forgot'
            component={ForgotDisplay}
          />
          <Route
            exact
            path='/welcome'
            component={SuccessDisplay}
          />
          <Route
            exact
            path='/intro_survey'
            component={PQL}
          />
          <Route
            exact
            path='/subscribe'
            render={() => (
              <SubscriptionDisplay
                promo={promo}
                search={location.search}
              />
            )}
          />
          <Route
            exact
            path='/'
            component={NetworkLayer}
          />
          <Route
            path='/chat'
            component={NetworkLayer}
          />
          <Route
            path='/projects/:projectID/codes/:codeID'
            component={NetworkLayer}
            exact
            />
          <Route
            path='/projects/:projectID'
            component={NetworkLayer}
            />
          <Route
            exact
            path='/projects'
            component={NetworkLayer}
          />
          <Route
            path='/projects/'
            component={NetworkLayer}
            />
          <Route
            path='/projects/:projectID/chat/:conversationID'
            component={NetworkLayer}
          />
          <Route
            path='/transcripts/:transcriptID'
            component={NetworkLayer}
          />
          <Route
            path='/transcripts/'
            component={NetworkLayer}
            />
          <Route
            path='/codes/:codeID'
            component={NetworkLayer}
          />
          <Route
            path='/users/'
            component={NetworkLayer}
            />
          <Route
            path='/surveys/'
            component={NetworkLayer}
            />
          <Route
            path='/survey_responses/:surveyResponseID'
            component={NetworkLayer}
          />
          <Route
            path='/survey_questions/:surveyQuestionID'
            component={NetworkLayer}
          />
          {/* Default catch-all route - redirects any unmatched path to root which will redirect from there */}
          <Route render={() => <Redirect to="/" />} />
        </Switch>
      </Box>
      
      {/* AI Chat Drawer - available throughout the app when logged in */}
      <AIChatDrawer />
    </Box>
  );
};

export default AppBody;